// ── participant-section-shell: the shared honest-state section wrapper (Wave 9) ─
// Every Participant Dashboard section is its OWN component wrapping its OWN
// useHonestQuery -> <HonestState> read (gold pattern, mirroring cohort-tab-shell).
// This thin shell renders the section chrome (a Module card + head) and routes
// the body through the honest state: the children render ONLY when posture is
// null (ready); otherwise the honest state shows (loading = skeleton, never a
// blank; a dark/missing lane = honest unavailable/not-available/not-configured,
// never fabricated data).

import type { ReactNode } from "react";
import { Module, ModuleBody, ModuleHead } from "@vjal/ui";

import { HonestState } from "../../components/honest-state";
import type { HonestGate } from "../../services/coaching-error-posture";
import type { HonestPosture } from "../../components/honest-state";

export interface ParticipantSectionShellProps {
  title: string;
  subtitle?: string;
  /** Optional harvest-stable name for the section heading. */
  titleExactName?: string;
  /** null = ready-with-data (render children); otherwise the honest state shows. */
  posture: HonestPosture | null;
  gate?: HonestGate | null;
  reference?: string | null;
  onRetry?: () => void;
  /** Override the neutral empty heading/body for this read. */
  emptyTitle?: string;
  emptyMessage?: string;
  /** Domain glyph for the honest empty/gated body (navy tint circle). */
  icon?: ReactNode;
  /** A testid stamp so the e2e/isolation specs can target the section. */
  testId?: string;
  /** Rendered only when posture === null (ready-with-data). */
  children: ReactNode;
}

export function ParticipantSectionShell({
  title,
  subtitle,
  titleExactName,
  posture,
  gate,
  reference,
  onRetry,
  emptyTitle,
  emptyMessage,
  icon,
  testId,
  children,
}: ParticipantSectionShellProps) {
  return (
    <Module span={12}>
      <ModuleHead title={title} subtitle={subtitle} titleExactName={titleExactName} />
      <ModuleBody className="space-y-4" data-testid={testId}>
        {posture !== null ? (
          <div data-honest-posture={posture}>
            <HonestState
              posture={posture}
              gate={gate}
              reference={reference}
              onRetry={onRetry}
              icon={icon}
              loadingVariant="rows"
              title={posture === "empty" ? emptyTitle : undefined}
              message={posture === "empty" ? emptyMessage : undefined}
            />
          </div>
        ) : (
          children
        )}
      </ModuleBody>
    </Module>
  );
}
